import { useState } from 'react';
import { testimonials } from '../../../mocks/testimonials';

function StarRating({ count = 5 }: { count?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="w-4 h-4 flex items-center justify-center text-brand-orange">
          <i className="ri-star-fill text-sm"></i>
        </span>
      ))}
    </div>
  );
}

function TestimonialCard({ item }: { item: typeof testimonials[number] }) {
  const typeConfig = {
    text: { label: 'Metin Yorum', icon: 'ri-chat-quote-line', color: 'text-brand-orange bg-brand-orange/10' },
    whatsapp: { label: 'WhatsApp Mesajı', icon: 'ri-whatsapp-line', color: 'text-whatsapp bg-whatsapp/10' },
    instagram: { label: 'Instagram Yorumu', icon: 'ri-instagram-line', color: 'text-pink-500 bg-pink-50' },
  };

  const config = typeConfig[item.type as keyof typeof typeConfig];

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-brand-orange/20 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <StarRating count={item.rating} />
        <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${config.color}`}>
          <span className="w-3 h-3 flex items-center justify-center">
            <i className={`${config.icon}`}></i>
          </span>
          {config.label}
        </span>
      </div>

      {/* Quote */}
      <div className="flex-1 mb-5">
        <span className="w-6 h-6 flex items-center justify-center text-brand-orange/30 mb-2">
          <i className="ri-double-quotes-l text-3xl"></i>
        </span>
        <p className="text-gray-600 text-sm leading-relaxed">{item.text}</p>
      </div>

      {/* Author */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-50">
        <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
          <img src={item.avatar} alt={item.name} className="w-full h-full object-cover object-top" />
        </div>
        <div className="min-w-0">
          <div className="text-brand-navy font-semibold text-sm truncate">{item.name}</div>
          <div className="text-gray-400 text-xs truncate">{item.location}</div>
        </div>
        <div className="ml-auto text-gray-300 text-xs whitespace-nowrap">{item.date}</div>
      </div>
    </div>
  );
}

export default function TestimonialsSection() {
  const [page, setPage] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);
  const visible = testimonials.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage);

  return (
    <section id="testimonials" className="py-20 md:py-28 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-brand-orange font-semibold text-sm uppercase tracking-widest mb-3">Müşteri Yorumları</p>
            <h2 className="text-brand-navy font-heading font-bold text-3xl md:text-4xl">
              Müşterilerimiz
              <br />
              Ne Diyor?
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-3xl font-heading font-bold text-brand-navy">5.0</div>
              <div className="flex items-center gap-1 justify-end mt-1">
                {[1,2,3,4,5].map(i => (
                  <span key={i} className="w-3 h-3 flex items-center justify-center text-brand-orange">
                    <i className="ri-star-fill text-xs"></i>
                  </span>
                ))}
              </div>
              <div className="text-gray-400 text-xs mt-1">500+ Mutlu Müşteri</div>
            </div>
          </div>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {visible.map((item) => (
            <TestimonialCard key={item.id} item={item} />
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between">
          <div className="text-gray-400 text-sm">
            {page * itemsPerPage + 1}–{Math.min((page + 1) * itemsPerPage, testimonials.length)} / {testimonials.length} yorum
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:border-brand-orange hover:text-brand-orange transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${i === page ? 'bg-brand-orange w-6' : 'bg-gray-200'}`}
              />
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page === totalPages - 1}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:border-brand-orange hover:text-brand-orange transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
          </div>
        </div>

        {/* Bottom CTA — kurumsal koyu tasarım */}
        <div className="mt-14 bg-gray-950 rounded-lg p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6 border border-gray-800">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-0.5 bg-brand-orange"></div>
              <span className="text-brand-orange text-xs font-semibold uppercase tracking-widest">Hemen Başlayın</span>
            </div>
            <h3 className="text-white font-heading font-bold text-2xl md:text-3xl mb-2">
              Siz de Töle Lojistik Ailesine Katılın
            </h3>
            <p className="text-gray-400 text-sm">Binlerce mutlu müşteri gibi siz de güvenilir taşımacılık deneyimi yaşayın.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="https://wa.me/905322376576"
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-whatsapp hover:bg-whatsapp-dark text-white px-6 py-3 rounded-md font-semibold transition-all hover:scale-105 cursor-pointer whitespace-nowrap text-sm"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line"></i>
              </span>
              WhatsApp'tan Yaz
            </a>
            <a
              href="tel:+905322376576"
              className="flex items-center gap-2 bg-transparent hover:bg-white/10 text-white border border-white/30 hover:border-white px-6 py-3 rounded-md font-semibold transition-all cursor-pointer whitespace-nowrap text-sm"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line"></i>
              </span>
              Hemen Ara
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
